import React, { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, Type, FileText, Info, UserSquareIcon, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner, Tooltip } from "@/components/ui/modal";
import { AvatarUpload } from "./avatar-upload";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ClearIcon, HIcon } from "../agent-dashboard-user-detail/assets";
import { AgentData } from "@/services/agent-list";
import { useToast } from "@/hooks/use-toast";
import { TextAreaInput } from "../common/text-area-input";
import { agentsHooks } from "@/manager";
import { LoadingOverlay } from "../common/loading-overlay";
import { ModalCloseButton } from "../common/modal-close-button";
import { FileIcon, TIcon, TipsIcon } from "./assets";
import { UserIdCardIcon } from "./assets/assets/user-id-card-icon";

export interface PanelAgentEditProps {
  open: boolean;
  name: string;
  intro?: string;
  agentId?: string;
  avatar?: string;
  agent?: AgentData;
  introductionTemplate?: string;
  introduction?: string;
  onOpenChange: (open: boolean) => void;
}

export const PanelAgentEdit: React.FC<PanelAgentEditProps> = ({
  open,
  onOpenChange,
  agentId,
  name: initialName,
  intro: initialIntro,
  avatar: initialAvatar,
  introduction: initialIntroduction,
  introductionTemplate,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { save, loading: saveLoading } = agentsHooks.useAgentUserinfoSave();
  const { upload: uploadAvatar, loading: uploadLoading } = agentsHooks.useUploadUserAvatar();

  const [name, setName] = useState(initialName);
  const [avatar, setAvatar] = useState(initialAvatar);
  const [intro, setIntro] = useState(initialIntro || "");
  const [introduction, setIntroduction] = useState(initialIntroduction || "");

  useEffect(() => {
    if (!open) return;
    setName(initialName ?? "");
    setAvatar(initialAvatar ?? "");
    setIntro(initialIntro ?? "");
    setIntroduction(initialIntroduction ?? "");
  }, [open, initialName, initialAvatar, initialIntro, initialIntroduction]);

  // 上传头像
  const onAvatarUpload = async (file: File) => {
    uploadAvatar(file).then((res) => {
      if (res) {
        setAvatar(res);
      }
    })
  };

  // 保存
  const handleSave = async () => {
    try {
      await save({
        subName: name,
        subIntro: intro,
        subAvatar: avatar,
        subIntroduction: introduction,
      });
      onOpenChange(false);
      toast.default({
        description: t('agent_creation.subscription_settings.save_success'),
      })
    } catch (error) {
      console.error("Failed to save agent user info:", error);
    }
  };

  // 使用示例模板
  const onUseTemplate = () => {
    if (!introductionTemplate) {
      toast.default({
        description: t('agent_dashboard.user_detail.agent_info.no_template'),
      })
      return;
    }

    // 字符数长度限制 20000
    if (introduction?.length >= 20000) {
      toast.default({
        description: t('agent_dashboard.user_detail.agent_info.intro_maximum'),
      })
      return;
    }

    // 如果原信息+模板 超过20000，则截取模板的部分内容
    if (introduction?.length + introductionTemplate.length > 20000) {
      const length = 20000 - introduction?.length;
      const truncatedTemplate = introductionTemplate.slice(0, length - 1);
      setIntroduction(introduction
        ? introduction + '\n' + truncatedTemplate
        : truncatedTemplate,)
      return;
    }

    // 正常情况，添加模板
    setIntroduction(introduction
      ? introduction + '\n' + introductionTemplate
      : introductionTemplate,)
  }

  const disabled = useMemo(() => {
    const noChangeName = name === initialName;
    const noChangeIntro = intro === initialIntro;
    const noChangeIntroduction = introduction === initialIntroduction;
    const onChangeAvatar = avatar === initialAvatar;

    return saveLoading || uploadLoading || (noChangeName && noChangeIntro && noChangeIntroduction && onChangeAvatar);
  }, [
    saveLoading, uploadLoading, name,
    intro, introduction, initialName,
    initialIntro, initialIntroduction, avatar, initialAvatar
  ]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/0" />
        <Dialog.Content
          className="
            fixed right-0 top-0 z-50 h-full w-[480px] bg-white
            flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300
          "
        >
          {/* 顶栏 */}
          <div className="flex items-center justify-between p-6 pb-0">
            <ModalCloseButton
              icon={<ChevronLeft className="w-5 h-5" />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* 头像与表单 */}
          <div className="flex-1 overflow-auto px-6 py-6">
            {/* 头像上传 */}
            <div className="flex justify-center mb-6">
              <Tooltip
                side="top"
                align="center"
                mode="hover"
                offset={0}
                trigger={
                  <div className="inline-block relative">
                    <AvatarUpload
                      className="m-0"
                      avatar={avatar}
                      title={name || initialName || "U"}
                      onUpload={onAvatarUpload}
                    />
                    <LoadingOverlay
                      loading={uploadLoading} />
                  </div>
                }
                content={
                  <div className="relative">
                    <div className="px-3 py-2 rounded-[8px] bg-[rgba(16,24,40,1)] text-sm font-medium text-white">
                      {t("agent_creation.agent_modal.change_image")}
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                  </div>
                }
              />
            </div>

            {/* 名字 */}
            <div className="mb-5">
              <label className="flex items-center gap-2 text-sm text-[#111827] mb-2">
                <TIcon className="size-5 text-[#111827]" />
                {t("agent_creation.agent_modal.name")}
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("agent_creation.agent_modal.name_placeholder")}
                className="
                  w-full h-12 px-3 rounded-md border border-[#e5e7eb]
                  text-[14px] text-[#111827] outline-none focus:border-[#9ca3af]
                "
              />
            </div>

            {/* 头衔 */}
            <div className="mb-5">
              <label className="flex items-center gap-2 text-sm text-[#111827] mb-2">
                <UserIdCardIcon className="size-5 text-[#111827]" />
                {t("agent_creation.agent_modal.intro")}
              </label>
              <textarea
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                placeholder={t("agent_creation.agent_modal.intro_placeholder")}
                rows={4}
                className="
                  w-full px-3 py-2 rounded-md border border-[#e5e7eb]
                  text-[14px] text-[#111827] outline-none focus:border-[#9ca3af] resize-none
                "
              />
            </div>

            {/* 简介 */}
            <div className="mb-5 ">
              <label className="flex items-center gap-2 text-sm text-[#111827] mb-2">
                <FileIcon className="size-5 text-[#111827]" />
                {t("agent_creation.agent_modal.introduction")}
              </label>
              <div className={cn(
                "w-full h-[303px] border border-[rgba(208,213,221,1)] rounded-[12px] overflow-hidden py-1 pt-3 pb-2 ",
                "transition-colors focus-within:border-[#9ca3af]"
              )}>
                <TextAreaInput
                  type="text"
                  value={introduction}
                  placeholder={t('agent_dashboard.user_detail.agent_info.intro_placeholder')}
                  autoFocus
                  className='h-[14.9rem] outline-none border-none pt-0 !font-normal !text-sm'
                  onChange={(e) => setIntroduction(e.target.value)}
                  maxLength={20000}
                />
                <div className='flex items-center justify-between px-4 mt-3'>
                  <div
                    className='w-[94px] h-8 bg-[rgba(242,244,247,1)] rounded-[8px] text-[13px] font-semibold text-[rgba(16,24,40,1)] flex items-center justify-center hover:bg-gray-200 cursor-pointer transition-all'
                    onClick={onUseTemplate}
                  >
                    <div className='flex items-center gap-1'>
                      <HIcon />
                      {t('agent_dashboard.user_detail.agent_info.example_title')}
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium text-[#98a2b3]'>{introduction?.length || 0}/20000</span>
                    <div
                      onClick={() => setIntroduction('')}
                    >
                      <ClearIcon className={cn(
                        'size-5 text-[rgba(208,213,221,1)]',
                        introduction?.length && 'text-[rgba(102,112,133,1)]  cursor-pointer'
                      )} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 提示 */}
            <div className="flex justify-center items-center gap-2">
              <TipsIcon className="size-5 text-[rgba(102,112,133,1)]" />
              <span className="text-sm text-[rgba(102,112,133,1)]">{t("agent_creation.agent_modal.save_tips")}</span>
            </div>
          </div>

          {/* 底部操作 */}
          <div className="shrink-0 p-4 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-10"
            >
              {t("agent_creation.agent_modal.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={disabled || saveLoading}
              className="h-10 text-white bg-[rgba(16,24,40,1)] hover:bg-[rgba(16,24,40,1)]/90"
            >
              {uploadLoading || saveLoading ? (
                <Spinner className="w-4 h-4" />
              ) : (
                t("agent_creation.agent_modal.save")
              )}
            </Button>
          </div>


        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};